/**
 * Pure sort logic for the per-tenant billing table. Framework-free so the
 * comparison is unit-testable in isolation and the component stays declarative.
 */

import type { IPlatformTenantRow } from "../../../../lib/api/platform-billing"
import type { TSortDir, TSortKey } from "./interfaces"

/** The default column + direction: highest revenue first, mirroring the backend order. */
export const DEFAULT_SORT_KEY: TSortKey = "billableUsd"
export const DEFAULT_SORT_DIR: TSortDir = "desc"

/**
 * Compare two tenant rows by the active sort column. `name` and `tenantId` sort
 * lexicographically (a missing name sorts as empty); every other column is
 * numeric. Ties fall back to a stable `tenantId` compare so the order is
 * deterministic across renders/runtimes.
 */
export function compareRows(a: IPlatformTenantRow, b: IPlatformTenantRow, key: TSortKey, dir: TSortDir): number {
  const sign = dir === "asc" ? 1 : -1
  if (key === "tenantId") {
    return sign * a.tenantId.localeCompare(b.tenantId)
  }
  if (key === "name") {
    const primary = (a.name ?? "").localeCompare(b.name ?? "")
    if (primary !== 0) return sign * primary
    return a.tenantId.localeCompare(b.tenantId)
  }
  const primary = a[key] - b[key]
  if (primary !== 0) return sign * primary
  return a.tenantId.localeCompare(b.tenantId)
}

/** Apply a sort to a copy of the rows (never mutates the input array). */
export function sortRows(rows: IPlatformTenantRow[], key: TSortKey, dir: TSortDir): IPlatformTenantRow[] {
  return [...rows].sort((a, b) => compareRows(a, b, key, dir))
}
